import { useEffect, useMemo } from 'react';
import { Separator } from "@/components/ui/separator"
import { useNotes } from "../contexts/NotesContext"
import { Button } from '@/components/ui/button';
import { useCourses } from '@/lib/courses';
import { useCommunication } from '@/lib/CommunicationWrapper';
import { useAuth } from '@/lib/auth';

const CourseItem = ({ id, color, title, count, active, numNotes }: { id: number, color: string, title: string, count: number, active: boolean, numNotes: string }) => {

	const { setActiveCourse } = useNotes();
	const { updateOptions } = useCommunication();

	const _setActiveCourse = (id: number) => {
		setActiveCourse(id);
		updateOptions({ notes_last_active_course: id });
	}

	return (
		<button className={`cursor-pointer w-full flex items-center justify-between text-base rounded-lg py-1.5 px-3 transition-all duration-200 border ${active ? "bg-theme-0 border-theme" : "hover:bg-theme-1 border-transparent"}`} onClick={() => _setActiveCourse(id)}>
			<div className="flex items-center justify-between gap-[10px]">
				<div className="w-[10px] h-[10px] rounded-full" style={{ backgroundColor: color }} />
				<p className="font-sm text-theme-0">{title}</p>
			</div>
			<span className="font-medium text-xs">{numNotes}</span>
		</button>
	)
}

const CoursesSidebarItems = () => {

	const { activeCourse, noteCounts } = useNotes();
	const { courseData, visibleCourseIds } = useCourses();

	const render = useMemo(() => {
		const courseItems = visibleCourseIds.map((courseId: any) => {
			const course = courseData?.[courseId];
			if (!course) return null;

			const count = noteCounts[courseId] || 0;
			return (
				<CourseItem numNotes={count.toString()} id={courseId} active={courseId === activeCourse} key={course.code !== "" ? course.code : course.default} color={course.color} title={course.code !== "" ? course.code : course.default} count={course.count} />)
		});

		// Add Non-class folder at the beginning
		const nonClassItem = (
			<CourseItem
				key="non-class"
				numNotes={(noteCounts[1] || 0).toString()}
				id={1}
				active={1 === activeCourse}
				color="#9ca3af"
				title="Non-class"
				count={0}
			/>
		);

		return [...courseItems, nonClassItem];
	}, [courseData, visibleCourseIds, activeCourse, noteCounts]);

	return (
		<div className="flex flex-col gap-1 flex-1">
			{render}
		</div>
	)
}

export const NotesUsageLimit = () => {

	const { planType } = useAuth();

	const { currentUsageBytes, maxLimitBytes } = useNotes();

	if (planType !== "free" && planType !== "style") return null;

	const mbUsed = currentUsageBytes / 1024 / 1024;
	const mbLimit = maxLimitBytes / 1024 / 1024;

	return (
		<div className="min-h-min w-full backdrop-filter backdrop-invert-[.02]">
			<p className="text-sm">{mbUsed.toFixed(1)} / {mbLimit} mb used</p>
			<div className="w-full h-2 bg-theme-1 border border-theme rounded-full overflow-hidden mt-1">
				<div
					className="h-full bg-theme-0 transition-all duration-300 rounded-full"
					style={{ width: `${Math.min((mbUsed / mbLimit) * 100, 100)}%` }}
				/>
			</div>
		</div>
	)
}

export const CoursesBrowser = () => {

	const { setActiveCourse, activeCourse } = useNotes();
	const { visibleCourseIds } = useCourses();
	const { getOptions } = useCommunication();

	// Count visible courses
	const visibleCoursesCount = useMemo(() => {
		return visibleCourseIds.length;
	}, [visibleCourseIds]);

	useEffect(() => {
		const options = getOptions();
		if (options?.notes_last_active_course) {
			setActiveCourse(options.notes_last_active_course);
		} else if (!activeCourse && visibleCourseIds.length > 0) {
			setActiveCourse(visibleCourseIds[0]);
		}
	}, [visibleCourseIds, getOptions]);

	return (
		<>
			<div className="w-full h-full flex flex-col backdrop-filter backdrop-invert-[.02] p-5">
				<h3 className="text-md font-semibold">Notes</h3>
				<Button variant="ghost" className="w-full mt-[20px] flex justify-between items-center text-base font-medium" onClick={() => setActiveCourse("all")}>
					<p className="font-normal">All courses</p>
					<span className="font-medium text-sm">{visibleCoursesCount}</span>
				</Button>
				<Separator className="my-2 bg-theme-border" />
				<CoursesSidebarItems />
				<NotesUsageLimit />
			</div>
		</>
	)
}